/**
 * Feb 2026 · Project File Manager.
 *
 * Renamed from "Attachments" — now a proper file+link manager with:
 *   • 1-level virtual folders (sidebar)
 *   • Unified list of files & links (paperclip vs link icon)
 *   • Description + tags + rename
 *   • Auto-fetched title + favicon on link create
 */
import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  FolderPlus, Folder, Upload, Link as LinkIcon, Paperclip, Search,
  Trash2, Download, Pencil, Tag as TagIcon, ExternalLink, X, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { fmtRelative } from "@/lib/format";

const MAX_BYTES = 25 * 1024 * 1024;

export default function ProjectFilesTab({ projectId }) {
  const { user } = useAuth();
  const [folders, setFolders] = useState([]);
  const [rootCount, setRootCount] = useState(0);
  const [items, setItems] = useState([]);           // files + links (unified)
  const [busy, setBusy] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState("__all__");   // "__all__", "root", or folder id
  const [q, setQ] = useState("");
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [showLink, setShowLink] = useState(false);
  const [editing, setEditing] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    setBusy(true);
    try {
      const [fRes, iRes] = await Promise.all([
        api.get(`/projects/${projectId}/folders`),
        api.get(`/projects/${projectId}/files`),
      ]);
      setFolders(fRes.data?.rows || []);
      setRootCount(fRes.data?.root_count || 0);
      setItems(iRes.data?.rows || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load files")); }
    finally { setBusy(false); }
  };
  useEffect(() => { if (projectId) load(); /* eslint-disable-next-line */ }, [projectId]);

  const filtered = useMemo(() => {
    let list = items;
    if (selectedFolder === "root") {
      list = list.filter((r) => !r.folder_id);
    } else if (selectedFolder !== "__all__") {
      list = list.filter((r) => r.folder_id === selectedFolder);
    }
    if (q) {
      const s = q.toLowerCase();
      list = list.filter((r) => {
        return (r.filename || "").toLowerCase().includes(s)
          || (r.link_title || "").toLowerCase().includes(s)
          || (r.description || "").toLowerCase().includes(s)
          || (r.tags || []).some((t) => t.toLowerCase().includes(s));
      });
    }
    return list;
  }, [items, selectedFolder, q]);

  const totalCount = items.length;

  const handleFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setUploading(true);
    const folderId = selectedFolder && selectedFolder !== "__all__" && selectedFolder !== "root" ? selectedFolder : null;
    let ok = 0;
    try {
      for (const f of Array.from(fileList)) {
        if (f.size > MAX_BYTES) { toast.error(`${f.name} is over 25 MB`); continue; }
        try {
          const body = new FormData();
          body.append("file", f);
          const { data } = await api.post(`/attachments/project/${projectId}`, body, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          if (folderId && data?.id) {
            await api.patch(`/project-files/${data.id}`, { folder_id: folderId });
          }
          ok += 1;
        } catch (e) { toast.error(apiErrorMessage(e, `Could not upload ${f.name}`)); }
      }
      if (ok > 0) toast.success(`${ok} file${ok === 1 ? "" : "s"} uploaded`);
      await load();
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const download = async (a) => {
    if (a.kind === "link") { window.open(a.link_url, "_blank", "noopener"); return; }
    try {
      const resp = await api.get(`/attachments/file/${a.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: a.content_type }));
      const el = document.createElement("a");
      el.href = url; el.download = a.filename; el.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErrorMessage(e, "Download failed")); }
  };

  const remove = async (a) => {
    if (!confirm(`Delete "${a.filename || a.link_title}"?`)) return;
    try {
      await api.delete(`/attachments/${a.id}`);
      toast.success("Deleted");
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  const deleteFolder = async (f) => {
    if (!confirm(`Delete folder "${f.name}"? Files inside will be moved to root.`)) return;
    try {
      await api.delete(`/project-folders/${f.id}`);
      toast.success("Folder deleted");
      if (selectedFolder === f.id) setSelectedFolder("__all__");
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  return (
    <div className="grid grid-cols-12 gap-4" data-testid="project-files">
      {/* Sidebar — folders */}
      <aside className="col-span-12 md:col-span-3 border border-border p-2 space-y-1" data-testid="project-files-sidebar">
        <FolderRow label="All items" count={totalCount} icon={Folder}
                    active={selectedFolder === "__all__"}
                    onClick={() => setSelectedFolder("__all__")}
                    testid="folder-all" />
        <FolderRow label="Root (no folder)" count={rootCount} icon={Folder}
                    active={selectedFolder === "root"}
                    onClick={() => setSelectedFolder("root")}
                    testid="folder-root" />
        <div className="border-t border-border pt-1 mt-1 label-eyebrow flex items-center gap-1 px-1">
          <Folder className="w-3 h-3" /> Folders
        </div>
        {folders.length === 0 && <div className="text-[10px] text-muted-foreground italic px-2 py-1">No folders yet.</div>}
        {folders.map((f) => (
          <div key={f.id} className={`group flex items-center border ${selectedFolder === f.id ? "border-primary bg-primary/10" : "border-transparent hover:border-border hover:bg-muted"}`} data-testid={`folder-${f.id}`}>
            <button className="flex-1 text-left px-2 py-1.5 text-xs truncate flex items-center gap-1" onClick={() => setSelectedFolder(f.id)}>
              <Folder className="w-3 h-3 shrink-0" /> {f.name}
              <span className="ml-auto text-[9px] text-muted-foreground">{f.item_count || 0}</span>
            </button>
            <button className="opacity-0 group-hover:opacity-100 px-1.5 text-muted-foreground hover:text-rose-600" onClick={() => deleteFolder(f)} title={`Delete folder ${f.name}`} data-testid={`folder-delete-${f.id}`}>
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        ))}
        <Button variant="outline" size="sm" className="w-full rounded-none mt-2 text-xs" onClick={() => setShowNewFolder(true)} data-testid="project-files-new-folder">
          <FolderPlus className="w-3 h-3 mr-1" /> New folder
        </Button>
      </aside>

      {/* Main — toolbar + item grid */}
      <div className="col-span-12 md:col-span-9 space-y-3">
        <div className="border border-border bg-muted/30 p-2 flex items-center gap-2 flex-wrap" data-testid="project-files-toolbar">
          <Search className="w-3.5 h-3.5" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search filename, description, tags…" className="h-8 w-72 text-xs" data-testid="project-files-search" />
          <span className="text-[10px] text-muted-foreground">{filtered.length} of {totalCount}</span>
          <div className="ml-auto flex items-center gap-2">
            <input type="file" ref={fileRef} className="hidden" multiple onChange={(e) => handleFiles(e.target.files)} data-testid="project-files-input" />
            <Button size="sm" className="rounded-none h-8 text-xs" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="project-files-upload">
              {uploading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />}
              Upload
            </Button>
            <Button size="sm" variant="outline" className="rounded-none h-8 text-xs" onClick={() => setShowLink(true)} data-testid="project-files-add-link">
              <LinkIcon className="w-3 h-3 mr-1" /> Add link
            </Button>
          </div>
        </div>

        {busy ? (
          <div className="p-6 text-center text-muted-foreground text-xs">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="border border-dashed border-border p-8 text-center text-muted-foreground text-xs">
            No files or links in this view. {selectedFolder === "__all__" ? "Upload something above or add an external link." : "Move items here from All items."}
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
            {filtered.map((it) => (
              <FileCard
                key={it.id}
                item={it}
                folders={folders}
                onEdit={() => setEditing(it)}
                onDownload={() => download(it)}
                onDelete={() => remove(it)}
              />
            ))}
          </div>
        )}
      </div>

      {showNewFolder && (
        <NewFolderDialog projectId={projectId} onClose={() => setShowNewFolder(false)}
                          onCreated={(f) => { setShowNewFolder(false); setFolders((prev) => [...prev, f].sort((a, b) => a.name.localeCompare(b.name))); setSelectedFolder(f.id); }} />
      )}
      {showLink && (
        <AddLinkDialog projectId={projectId} folders={folders}
                        defaultFolderId={selectedFolder && selectedFolder !== "__all__" && selectedFolder !== "root" ? selectedFolder : null}
                        onClose={() => setShowLink(false)}
                        onCreated={() => { setShowLink(false); load(); }} />
      )}
      {editing && (
        <EditFileDialog item={editing} folders={folders}
                        onClose={() => setEditing(null)}
                        onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function FolderRow({ label, count, icon: Icon, active, onClick, testid }) {
  return (
    <button className={`w-full text-left flex items-center gap-1.5 px-2 py-1.5 text-xs border ${active ? "border-primary bg-primary/10" : "border-transparent hover:border-border hover:bg-muted"}`} onClick={onClick} data-testid={testid}>
      <Icon className="w-3 h-3 shrink-0" /> <span className="truncate">{label}</span>
      <span className="ml-auto text-[9px] text-muted-foreground">{count}</span>
    </button>
  );
}

function FileCard({ item, folders, onEdit, onDownload, onDelete }) {
  const isLink = item.kind === "link";
  const title = isLink ? (item.link_title || item.link_url) : item.filename;
  const folderName = folders.find((f) => f.id === item.folder_id)?.name;
  return (
    <div className="border border-border p-3 hover:border-primary/40 flex items-start gap-3" data-testid={`file-${item.id}`}>
      <div className="w-8 h-8 shrink-0 border border-border flex items-center justify-center bg-muted/30">
        {isLink ? (
          item.link_favicon ? <img src={item.link_favicon} alt="" className="w-4 h-4" onError={(e) => { e.target.style.display = "none"; }} />
                              : <LinkIcon className="w-4 h-4 text-primary" />
        ) : <Paperclip className="w-4 h-4 text-muted-foreground" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm truncate flex items-center gap-1.5" title={title}>
          {title}
          {isLink && <ExternalLink className="w-3 h-3 text-muted-foreground shrink-0" />}
        </div>
        {isLink && item.link_domain && (
          <div className="text-[10px] text-muted-foreground truncate">{item.link_domain}</div>
        )}
        {item.description && <div className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">{item.description}</div>}
        <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
          {(item.tags || []).map((t) => (
            <span key={t} className="chip text-[9px]" data-testid={`file-tag-${item.id}-${t}`}>{t}</span>
          ))}
          {folderName && <span className="chip chip-internal text-[9px]"><Folder className="w-2.5 h-2.5 inline mr-0.5" /> {folderName}</span>}
        </div>
        {/* R96 · Provenance — show "Received from" + date so audits see the source at-a-glance. */}
        {(item.received_from || item.received_date) && (
          <div className="text-[10px] text-muted-foreground mt-1" data-testid={`file-provenance-${item.id}`}>
            Received{item.received_from ? <> from <b className="text-foreground/80">{item.received_from}</b></> : ""}
            {item.received_date ? <> · {item.received_date}</> : ""}
          </div>
        )}
        <div className="text-[10px] text-muted-foreground mt-1.5">
          {item.uploader_name || "—"} · {fmtRelative(item.created_at)}
          {!isLink && item.size ? ` · ${Math.round(item.size / 1024)} KB` : ""}
        </div>
      </div>
      <div className="flex flex-col gap-1">
        <button className="p-1 hover:bg-muted" onClick={onEdit} title="Edit" data-testid={`file-edit-${item.id}`}><Pencil className="w-3.5 h-3.5" /></button>
        <button className="p-1 hover:bg-muted" onClick={onDownload} title={isLink ? "Open link" : "Download"} data-testid={`file-open-${item.id}`}>
          {isLink ? <ExternalLink className="w-3.5 h-3.5" /> : <Download className="w-3.5 h-3.5" />}
        </button>
        <button className="p-1 hover:bg-muted text-rose-600" onClick={onDelete} title="Delete" data-testid={`file-delete-${item.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
      </div>
    </div>
  );
}

function NewFolderDialog({ projectId, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/folders`, { name: name.trim() });
      toast.success("Folder created");
      onCreated?.(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Create failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm rounded-none" data-testid="project-folder-dialog">
        <DialogHeader><DialogTitle>New folder</DialogTitle></DialogHeader>
        <Input value={name} onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
                placeholder="e.g. Brand assets, Contracts, Wireframes"
                autoFocus className="rounded-none h-10"
                data-testid="project-folder-name" />
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={submit} disabled={busy || !name.trim()} data-testid="project-folder-save">
            {busy ? "Creating…" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddLinkDialog({ projectId, folders, defaultFolderId, onClose, onCreated }) {
  const [url, setUrl] = useState("");
  const [label, setLabel] = useState("");
  const [desc, setDesc] = useState("");
  const [tagsStr, setTagsStr] = useState("");
  const [folderId, setFolderId] = useState(defaultFolderId || "");
  // R96 · Provenance
  const [receivedFrom, setReceivedFrom] = useState("");
  const [receivedDate, setReceivedDate] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!url.trim()) { toast.error("URL required"); return; }
    setBusy(true);
    try {
      const tags = tagsStr.split(",").map((t) => t.trim()).filter(Boolean);
      await api.post(`/projects/${projectId}/links`, {
        url: url.trim(),
        label: label.trim() || null,
        description: desc.trim() || null,
        tags,
        folder_id: folderId || null,
        received_from: receivedFrom.trim() || null,
        received_date: receivedDate || null,
      });
      toast.success("Link added");
      onCreated?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Add failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md rounded-none" data-testid="project-link-dialog">
        <DialogHeader><DialogTitle className="inline-flex items-center gap-2"><LinkIcon className="w-4 h-4" /> Add external link</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-semibold">URL *</Label>
            <Input value={url} onChange={(e) => setUrl(e.target.value)}
                    placeholder="https://docs.google.com/…"
                    autoFocus className="h-10 rounded-none"
                    data-testid="project-link-url" />
            <p className="text-[10px] text-muted-foreground mt-1">We&apos;ll auto-fetch the title + favicon in the background.</p>
          </div>
          <div>
            <Label className="text-xs font-semibold">Label (optional)</Label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)}
                    placeholder="Override the auto-fetched title"
                    className="h-10 rounded-none" data-testid="project-link-label" />
          </div>
          <div>
            <Label className="text-xs font-semibold">Description</Label>
            <Textarea rows={2} value={desc} onChange={(e) => setDesc(e.target.value)}
                       data-testid="project-link-desc" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold">Tags (comma-separated)</Label>
              <Input value={tagsStr} onChange={(e) => setTagsStr(e.target.value)}
                      placeholder="brief, moodboard" className="h-10 rounded-none"
                      data-testid="project-link-tags" />
            </div>
            <div>
              <Label className="text-xs font-semibold">Folder</Label>
              <select value={folderId} onChange={(e) => setFolderId(e.target.value)}
                       className="w-full h-10 border border-border px-2 text-sm bg-background rounded-none"
                       data-testid="project-link-folder">
                <option value="">— Root —</option>
                {folders.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            </div>
          </div>
          {/* R96 · Provenance for incoming links (creative sent by agency, etc.) */}
          <div className="grid grid-cols-2 gap-3 pt-1 border-t border-border">
            <div>
              <Label className="text-xs font-semibold">Received from</Label>
              <Input
                value={receivedFrom}
                onChange={(e) => setReceivedFrom(e.target.value)}
                placeholder="Person, brand or agency"
                className="h-10 rounded-none"
                data-testid="project-link-received-from"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold">Received date</Label>
              <Input
                type="date"
                value={receivedDate}
                onChange={(e) => setReceivedDate(e.target.value)}
                className="h-10 rounded-none"
                data-testid="project-link-received-date"
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={submit} disabled={busy || !url.trim()} data-testid="project-link-save">
            {busy ? "Adding…" : "Add link"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditFileDialog({ item, folders, onClose, onSaved }) {
  const isLink = item.kind === "link";
  const [name, setName] = useState(isLink ? (item.link_title || "") : (item.filename || ""));
  const [desc, setDesc] = useState(item.description || "");
  const [tagsStr, setTagsStr] = useState((item.tags || []).join(", "));
  const [folderId, setFolderId] = useState(item.folder_id || "");
  // R96 · Provenance
  const [receivedFrom, setReceivedFrom] = useState(item.received_from || "");
  const [receivedDate, setReceivedDate] = useState((item.received_date || "").slice(0, 10));
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const tags = tagsStr.split(",").map((t) => t.trim()).filter(Boolean);
      const payload = {
        description: desc, tags,
        folder_id: folderId || "root",
        // Empty string clears; backend $unsets it.
        received_from: receivedFrom,
        received_date: receivedDate,
      };
      if (isLink) payload.link_title = name;
      else payload.filename = name;
      await api.patch(`/project-files/${item.id}`, payload);
      toast.success("Saved");
      onSaved?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md rounded-none" data-testid="project-file-edit-dialog">
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-2">
            {isLink ? <LinkIcon className="w-4 h-4" /> : <Paperclip className="w-4 h-4" />}
            Edit {isLink ? "link" : "file"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-semibold">{isLink ? "Label" : "Filename"}</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="h-10 rounded-none" data-testid="file-edit-name" />
          </div>
          <div>
            <Label className="text-xs font-semibold">Description</Label>
            <Textarea rows={3} value={desc} onChange={(e) => setDesc(e.target.value)} data-testid="file-edit-desc" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold">Tags</Label>
              <Input value={tagsStr} onChange={(e) => setTagsStr(e.target.value)} placeholder="brief, wip" className="h-10 rounded-none" data-testid="file-edit-tags" />
            </div>
            <div>
              <Label className="text-xs font-semibold">Folder</Label>
              <select value={folderId} onChange={(e) => setFolderId(e.target.value)}
                       className="w-full h-10 border border-border px-2 text-sm bg-background rounded-none"
                       data-testid="file-edit-folder">
                <option value="">— Root —</option>
                {folders.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            </div>
          </div>
          {/* R96 · Provenance so incoming assets carry their source into audits. */}
          <div className="grid grid-cols-2 gap-3 pt-1 border-t border-border">
            <div>
              <Label className="text-xs font-semibold">Received from</Label>
              <Input
                value={receivedFrom}
                onChange={(e) => setReceivedFrom(e.target.value)}
                placeholder="Person, brand or agency"
                className="h-10 rounded-none"
                data-testid="file-edit-received-from"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold">Received date</Label>
              <Input
                type="date"
                value={receivedDate}
                onChange={(e) => setReceivedDate(e.target.value)}
                className="h-10 rounded-none"
                data-testid="file-edit-received-date"
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={submit} disabled={busy} data-testid="file-edit-save">
            {busy ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
